import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import PropTypes from 'prop-types';
import Select from 'react-select';
import DatePicker, { registerLocale } from 'react-datepicker';
import InputMask from 'react-input-mask';
/* actions */
import { addSchedules } from '../../redux/actions/schedules';
import { getAllTypeSchedules } from '../../redux/actions/typeSchedules';
import { getAllUsers } from '../../redux/actions/users';
import { getAllClients } from '../../redux/actions/clients';
import { getAllPets } from '../../redux/actions/pets';
/* components */
import ErrorMsgField from '../../components/ErrorMsgField';
import AlertMsg from '../../components/AlertMsg';
import FormFooter from '../../components/FormFooter';
import FormHeader from '../../components/FormHeader';
import ModalAddTypeSchedule from '../../components/ModalAddTypeSchedule';
import ModalAddClient from '../../components/ModalAddClient';
import MultiSelect from '../../components/MultiSelect';
/* internal components */
import Clients from './Clients';
/* form-validation */
import schema from '../../form-validation/schedule';
/* utils */
import {
  customStyle,
  customThemes,
  customFilter,
  firstOption,
  handleChangeSelect
} from '../../utils/select';
import sortable from '../../utils/sortable';
import handleChangeDefault from '../../utils/handleChangeDefault';
import ptBR from 'date-fns/locale/pt-BR';
import {
  handleMultiSelect,
  handleMultiDeselect
} from '../../utils/multiSelect';
registerLocale('ptBR', ptBR);

function Add (props) {
  const { handleModalClose } = props;
  const dispatch = useDispatch();
  const submitButton = useRef();
  const { register, handleSubmit, errors, reset, setError, setValue } = useForm(
    {
      validationSchema: schema,
      reValidateMode: 'onSubmit'
    }
  );
  const sessionUser = useSelector(state => state.authentications.user);
  const company = sessionUser.company._id;
  const schedules = useSelector(state => state.schedules);
  const INITIAL_VALUES = {
    dayStart: new Date(),
    hourStart: new Date().toLocaleTimeString(),
    dayEnd: new Date(),
    hourEnd: new Date().toLocaleTimeString(),
    title: '',
    typeSchedule: '',
    description: '',
    local: '',
    employees: [],
    clients: [],
    pets: [],
    completed: false,
    company
  };
  const [data, setData] = useState(INITIAL_VALUES);
  const users = useSelector(state => state.users.data);
  const clients = useSelector(state => state.clients.data);
  const pets = useSelector(state => state.pets.data);
  const typeSchedules = useSelector(state => state.typeSchedules.data);
  const [typeScheduleOptions, setTypeScheduleOptions] = useState([firstOption]);
  const [showModalAddTypeSchedule, setShowModalAddTypeSchedule] = useState(
    false
  );
  const [showModalAddClient, setShowModalAddClient] = useState(false);
  const INITIAL_OPTIONS = { employees: [], clients: [], pets: [] };
  const [selectedOptions, setSelectedOptions] = useState(INITIAL_OPTIONS);
  const [usrs, setUsrs] = useState([]);
  const [pts, setPts] = useState([]);

  useEffect(() => {
    dispatch(getAllTypeSchedules({ company }));
    dispatch(getAllUsers({ company }));
    dispatch(getAllClients({ company }));
    dispatch(getAllPets({ company }));
  }, [dispatch]);

  useEffect(() => {
    const options = selectedOptions.pets.filter(pet => {
      return data.clients.find(cliendId => {
        return pet.responsibles.includes(cliendId);
      });
    });
    setSelectedOptions({ ...selectedOptions, pets: options });
    const pets = options.map(opt => opt.value);
    setData({ ...data, pets });
  }, [data.clients]);

  useEffect(() => {
    if (typeSchedules.length) {
      const options = typeSchedules
        .map(typeSchedule => ({
          value: typeSchedule._id,
          label: `${typeSchedule.name}`,
          sortValue: `${typeSchedule.name}`
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setTypeScheduleOptions([...options]);
    }
  }, [typeSchedules, setTypeScheduleOptions]);

  useEffect(() => {
    if (users.length) {
      const usrs = users
        .map(usr => ({
          value: usr._id,
          text: `${usr.name} | Dpto: ${usr.department.name}`,
          sortValue: usr.name
        }))
        .sort(sortable);

      setUsrs([...usrs]);
    }
  }, [users, setUsrs]);

  useEffect(() => {
    if (pets.length) {
      const pts = pets
        .map(pet => ({
          value: pet._id,
          text: `${pet.name}`,
          sortValue: pet.name,
          responsibles: pet.responsibles
        }))
        .sort(sortable);

      setPts([...pts]);
    }
  }, [pets, setPts]);

  const submit = () => {
    const resetForm = () => {
      reset();
      setSelectedOptions({ ...INITIAL_OPTIONS });
      setData({ ...INITIAL_VALUES });
    };
    const newData = { ...data };
    // Date Start
    const [startHours, startMinutes] = data.hourStart.split(':');
    const dateStart = new Date(data.dayStart);
    dateStart.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);
    delete newData.hourStart;
    delete newData.dayStart;
    newData.dateStart = dateStart;
    // Date End
    const [endHours, endMinutes] = data.hourEnd.split(':');
    const dateEnd = new Date(data.dayEnd);
    dateEnd.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);
    delete newData.hourEnd;
    delete newData.dayEnd;
    newData.dateEnd = dateEnd;
    dispatch(addSchedules(newData, resetForm, submitButton.current));
  };

  return (
    <div className="container-fluid">
      <ModalAddTypeSchedule
        showModal={showModalAddTypeSchedule}
        handleModalClose={() => setShowModalAddTypeSchedule(false)}
      />
      <ModalAddClient
        showModal={showModalAddClient}
        handleModalClose={() => setShowModalAddClient(false)}
      />
      {schedules.addSuccess && (
        <AlertMsg type="success" msg="Agendamento cadastrado com sucesso" />
      )}
      {schedules.errorContent &&
        !schedules.errorContent.message.includes('400') &&
        schedules.addError && (
        <AlertMsg type="danger" msg="Falha durante cadastro do agendamento" />
      )}
      {schedules.errorContent &&
        schedules.errorContent.message.includes('400') &&
        schedules.addError && (
        <AlertMsg
          type="danger"
          msg="Agendamento informado já está cadastrado"
        />
      )}
      {!schedules.addSuccess && !schedules.addError && (
        <AlertMsg type="info" msg="Área cadastro de agendamentos" />
      )}
      <FormHeader title="Cadastrar Agendamento" />
      <form onSubmit={handleSubmit(submit)}>
        <div className="row">
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="dayStart">
                Data de Início<span className="field-required">*</span>
              </label>
              <DatePicker
                className="form-control"
                name="dayStart"
                onChange={date => setData({ ...data, dayStart: date })}
                selected={data.dayStart}
                placeholderText="dd/MM/yyyy"
                locale="ptBR"
                dateFormat="dd/MM/yyyy"
                autoComplete="off"
                customInput={<input type="text" date={data.dayStart} />}
              />
              {errors.dayStart && errors.dayStart.type === 'date' && (
                <ErrorMsgField type="date" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="hourStart">
                Hora de Início<span className="field-required">*</span>
              </label>
              <InputMask
                id="hourStart"
                name="hourStart"
                maskChar=""
                mask="99:99"
                placeholder="HH:mm"
                className="form-control"
                autoComplete="off"
                value={data.hourStart}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.hourStart && errors.hourStart.type === 'hour' && (
                <ErrorMsgField type="hour" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="dayEnd">
                Data de Término<span className="field-required">*</span>
              </label>
              <DatePicker
                className="form-control"
                name="dayEnd"
                onChange={date => setData({ ...data, dayEnd: date })}
                selected={data.dayEnd}
                placeholderText="dd/MM/yyyy"
                locale="ptBR"
                dateFormat="dd/MM/yyyy"
                autoComplete="off"
                customInput={<input type="text" date={data.dayEnd} />}
              />
              {errors.dayEnd && errors.dayEnd.type === 'date' && (
                <ErrorMsgField type="date" />
              )}
              {errors.hourEnd && errors.hourEnd.type === 'endDate' && (
                <ErrorMsgField
                  type="endDate"
                  msg="O término deve ser maior que o início"
                />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="hourEnd">
                Hora de Término<span className="field-required">*</span>
              </label>
              <InputMask
                id="hourEnd"
                name="hourEnd"
                maskChar=""
                mask="99:99"
                placeholder="HH:mm"
                className="form-control"
                autoComplete="off"
                value={data.hourEnd}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.hourEnd && errors.hourEnd.type === 'hour' && (
                <ErrorMsgField type="hour" />
              )}
              {errors.hourEnd && errors.hourEnd.type === 'endDate' && (
                <ErrorMsgField
                  type="endDate"
                  msg="O término deve ser maior que o início"
                />
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="title">
                Título<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="title"
                name="title"
                autoComplete="off"
                value={data.title}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.title && errors.title.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-8">
            <div className="form-group">
              <div className="form-group form-inline mb-0">
                <label
                  className="col-sm-12 d-inline p-0 mb-2"
                  htmlFor="typeSchedule"
                >
                  Tipo de Agendamento
                  <span className="field-required">*</span>
                </label>
                <Select
                  className="col p-0"
                  name="typeSchedule"
                  ref={register}
                  onChange={evt =>
                    handleChangeSelect(
                      { ...evt, name: 'typeSchedule' },
                      data,
                      setData,
                      setError,
                      setValue
                    )
                  }
                  defaultValue={typeScheduleOptions[0]}
                  value={typeScheduleOptions.find(
                    typeSchedule => typeSchedule.value === data.typeSchedule
                  )}
                  options={typeScheduleOptions}
                  isSearchable={true}
                  theme={customThemes}
                  styles={customStyle}
                  filterOption={customFilter}
                  noOptionsMessage={() => 'Nenhum registro encontrado'}
                />
                <div className="pl-2">
                  <button
                    type="button"
                    className="btn btn-success"
                    title="Adicionar Tipo de Agendamento"
                    disabled={showModalAddTypeSchedule}
                    onClick={() => setShowModalAddTypeSchedule(true)}
                  >
                    <i className="fa fa-plus" />
                    &nbsp;Tipo de Agendamento
                  </button>
                </div>
              </div>
              {errors.typeSchedule &&
                errors.typeSchedule.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="description">
            Descrição<span className="field-required">*</span>
          </label>
          <textarea
            className="form-control"
            id="description"
            name="description"
            rows="3"
            value={data.description}
            onChange={evt => handleChangeDefault(evt, data, setData)}
            ref={register}
          ></textarea>
          {errors.description && errors.description.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <MultiSelect
          avaliableOptions={usrs}
          selectedOptions={selectedOptions.employees}
          handleMultiDeselect={options =>
            handleMultiDeselect(
              options,
              'employees',
              selectedOptions,
              setSelectedOptions,
              data,
              setData
            )
          }
          handleMultiSelect={options =>
            handleMultiSelect(
              options,
              'employees',
              selectedOptions,
              setSelectedOptions,
              data,
              setData
            )
          }
          valueProp="value"
          textProp="text"
          title="Colaboradores Envolvidos"
          fieldRequired={true}
          name="employees"
          ref={register}
          error={!!errors.employees}
        />
        <div>
          <button
            type="button"
            className="btn btn-success"
            title="Adicionar Cliente"
            disabled={showModalAddClient}
            onClick={() => setShowModalAddClient(true)}
          >
            <i className="fa fa-plus" />
            &nbsp;Cliente
          </button>
        </div>
        <Clients clientsData={clients} data={data} setData={setData} />
        <div className="form-group">
          <label htmlFor="local">Local</label>
          <input
            type="text"
            className="form-control"
            id="local"
            name="local"
            autoComplete="off"
            value={data.local}
            onChange={evt => handleChangeDefault(evt, data, setData)}
            ref={register}
          />
        </div>
        <MultiSelect
          avaliableOptions={pts.filter(pet => {
            return pet.responsibles.find(responsible => {
              return data.clients.includes(responsible);
            });
          })}
          selectedOptions={selectedOptions.pets}
          handleMultiDeselect={options =>
            handleMultiDeselect(
              options,
              'pets',
              selectedOptions,
              setSelectedOptions,
              data,
              setData
            )
          }
          handleMultiSelect={options =>
            handleMultiSelect(
              options,
              'pets',
              selectedOptions,
              setSelectedOptions,
              data,
              setData
            )
          }
          valueProp="value"
          textProp="text"
          title="Pets Envolvidos"
          name="pets"
          ref={register}
          error={!!errors.pets}
        />
        {handleModalClose ? (
          <div className="container-fluid p-0">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleModalClose}
            >
              <i className="fa fa-times"></i>
              &nbsp;Fechar
            </button>
            <button
              type="submit"
              ref={submitButton}
              className="btn btn-primary float-right"
            >
              <i className="fa fa-save"></i>
              &nbsp;Salvar
            </button>
          </div>
        ) : (
          <FormFooter url="/schedules" reference={submitButton} />
        )}
      </form>
    </div>
  );
}

Add.propTypes = {
  handleModalClose: PropTypes.func
};

export default Add;
